'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import {
  getStoredConsent,
  storeConsent,
  startAnalyticsIfConsented,
  stopAnalyticsAndClearCookies,
  type CookieConsentValue,
} from '@/lib/analytics-consent';

export const OPEN_COOKIE_SETTINGS_EVENT = 'talksie:open-cookie-settings';

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const consent = getStoredConsent();
    if (consent === null) {
      setVisible(true);
    } else if (consent === 'accepted') {
      void startAnalyticsIfConsented();
    }

    const handleReopen = () => setVisible(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleReopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleReopen);
  }, []);

  // Reserve space at the bottom of the page while the banner is open so it
  // never sits on top of buttons/content already on the page.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (!visible || !bannerRef.current) {
      document.body.style.paddingBottom = '';
      return;
    }

    const el = bannerRef.current;
    const updatePadding = () => {
      document.body.style.paddingBottom = `${el.offsetHeight}px`;
    };
    updatePadding();

    const observer = new ResizeObserver(updatePadding);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.body.style.paddingBottom = '';
    };
  }, [visible]);

  const handleChoice = useCallback((value: CookieConsentValue) => {
    const previousConsent = getStoredConsent();
    storeConsent(value);
    setVisible(false);

    if (value === 'accepted') {
      void startAnalyticsIfConsented();
      return;
    }

    if (previousConsent === 'accepted') {
      stopAnalyticsAndClearCookies();
      window.location.reload();
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-gray-800 bg-black px-4 py-4 text-white"
      style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
    >
      <div className="container mx-auto flex max-w-6xl flex-col items-center gap-3 sm:flex-row sm:justify-between">
        <p className="text-center text-sm text-gray-300 sm:text-left">
          We use cookies for analytics to improve Talksie. See our{' '}
          <Link href="/privacy-policy" className="underline hover:text-white">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex w-full gap-3 sm:w-auto">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 sm:w-28 sm:flex-none"
            onClick={() => handleChoice('rejected')}
          >
            Reject
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 sm:w-28 sm:flex-none"
            onClick={() => handleChoice('accepted')}
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
